"use client";

// ===================================================
// Global table appearance settings.
//
// Gal's rule (2026-07-10): "כשאני מדבר על הטבלה הזאת הכוונה לכל הטבלאות."
// One config row, read by every table surface. Change it once, every table in
// the system follows — that is the whole point, and it is why this lives in
// Supabase and not in localStorage. Vivaldi's documented failure is exactly
// per-device customization that never syncs (docs/BRAND.md research notes).
// ===================================================

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/lib/supabaseClient";

export type TableEngine = "classic" | "v2";
export type TableSkin = "quiet" | "pro" | "signature" | "soft";
export type TableAccent =
  | "teal" | "emerald" | "cyan" | "sky" | "indigo"
  | "violet" | "fuchsia" | "rose" | "slate" | "neutral";
export type TableDensity = "compact" | "comfortable";

export interface TableSettings {
  engine: TableEngine;
  skin: TableSkin;
  accent: TableAccent;
  density: TableDensity;
  zebra: boolean;
  sticky_head: boolean;
}

export const TABLE_SETTINGS_DEFAULTS: TableSettings = {
  engine: "classic",
  skin: "pro",
  accent: "teal",
  density: "comfortable",
  zebra: false,
  sticky_head: true,
};

/**
 * Accent maps to a CSS custom property, not a raw hex, so swapping the skin
 * or the brand palette later does not orphan the tables.
 * Brand v2: teal is the only filled brand colour. Orange is a line, never a fill —
 * that is why it is absent from this list.
 */
export const ACCENT_VARS: Record<TableAccent, string> = {
  teal: "20, 184, 166",
  emerald: "16, 185, 129",
  cyan: "6, 182, 212",
  sky: "14, 165, 233",
  indigo: "99, 102, 241",
  violet: "139, 92, 246",
  fuchsia: "217, 70, 239",
  rose: "244, 63, 94",
  slate: "100, 116, 139",
  neutral: "148, 163, 184",
};

/** Hebrew labels for the accent swatches. */
export const ACCENT_LABELS: Record<TableAccent, string> = {
  teal: "טיל",
  emerald: "אזמרגד",
  cyan: "טורקיז",
  sky: "תכלת",
  indigo: "אינדיגו",
  violet: "סגול",
  fuchsia: "פוקסיה",
  rose: "ורד",
  slate: "צפחה",
  neutral: "נייטרלי",
};

const QUERY_KEY = ["cc_table_settings"] as const;

export function useTableSettings() {
  return useQuery<TableSettings>({
    queryKey: QUERY_KEY,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cc_table_settings")
        .select("engine, skin, accent, density, zebra, sticky_head")
        .limit(1)
        .maybeSingle();

      // A missing row is not an error — fall back to defaults so a table never
      // fails to render because nobody has opened the settings screen yet.
      if (error || !data) return TABLE_SETTINGS_DEFAULTS;
      return data as TableSettings;
    },
  });
}

export function useUpdateTableSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<TableSettings>) => {
      const { data, error } = await supabase
        .from("cc_table_settings")
        .update({ ...patch, updated_at: new Date().toISOString() })
        .eq("tenant_id", "00000000-0000-0000-0000-000000000000")
        .select("engine, skin, accent, density, zebra, sticky_head")
        .single();

      // Silent-write check: a PATCH that matches zero rows returns no error.
      // Verify we got the row back rather than assuming success.
      if (error) throw error;
      if (!data) throw new Error("הגדרות הטבלה לא נשמרו — לא נמצאה שורה לעדכון");
      return data as TableSettings;
    },
    onSuccess: (data) => qc.setQueryData(QUERY_KEY, data),
  });
}
